'use client';

import {cn, date, SHORT_WEEK_NAMES} from '@rocket-ui/tools';
import range from 'lodash/range';
import React, {FC, useContext, useState} from 'react';
import {NewIconButton} from '../Button';

interface CalendarContextProps {
	value?: Date;
	monthStart: Date;
	minDate?: Date;
	maxDate?: Date;
}

const CalendarContext = React.createContext<CalendarContextProps | null>(null);

function useCalendarContext(): CalendarContextProps {
	const ctx = useContext(CalendarContext);

	if (!ctx) throw new Error('useCalendarContext must be used inside CalendarContext');

	return ctx;
}

export interface NewCalendarProps {
	value?: Date;
	onChange: (date: Date) => void;
	minDate?: Date;
	maxDate?: Date;
	highlightToday?: boolean;
	className?: string;
}

export const NewCalendar: FC<NewCalendarProps> = ({value, onChange, highlightToday, minDate, maxDate, className}) => {
	const [monthStart, setMonthStart] = useState(() => date.startOf.month(value || new Date()));
	const minDateStart = minDate ? date.startOf.day(minDate) : undefined;
	const maxDateEnd = maxDate ? date.endOf.day(maxDate) : undefined;

	return (
		<div className={cn('flex flex-col gap-2 w-full min-w-72', className)}>
			<CalendarContext.Provider value={{value, monthStart, minDate: minDateStart, maxDate: maxDateEnd}}>
				<CalendarHeader onChange={setMonthStart} />
				<WeekHeader className={'pb-2 border-b'} />
				<Days onSelect={onChange} highlightToday={highlightToday} />
			</CalendarContext.Provider>
		</div>
	);
};

const CalendarHeader: FC<{onChange: (date: Date) => void}> = ({onChange}) => {
	const {monthStart} = useCalendarContext();
	const monthName = date.format(monthStart, 'LLLL');
	const year = date.format(monthStart, 'yyyy');

	const handlePrev = () => onChange(date.add.month(monthStart, -1));
	const handleNext = () => onChange(date.add.month(monthStart, 1));

	return (
		<div className={'flex justify-center items-center gap-2 font-bold'}>
			<NewIconButton icon={'chevron-left'} onClick={handlePrev} size={'sm'} variant={'ghost'} />
			<p className={'capitalize'}>
				{monthName} {year}
			</p>
			<NewIconButton icon={'chevron-right'} onClick={handleNext} size={'sm'} variant={'ghost'} />
		</div>
	);
};

const WeekHeader: FC<{className?: string}> = ({className}) => (
	<div className={cn('grid grid-cols-7', className)}>
		{SHORT_WEEK_NAMES.map((name) => (
			<p key={name} className={'text-xs text-center uppercase'}>
				{name}
			</p>
		))}
	</div>
);

const Days: FC<{highlightToday?: boolean; onSelect: (date: Date) => void}> = ({highlightToday, onSelect}) => {
	const {value, monthStart, minDate, maxDate} = useCalendarContext();
	const today = new Date();
	const firstWeekDay = date.getWeekDay(monthStart);
	const daysInMonth = date.getDaysInMonth(monthStart);

	const toDate = (dayNumber: number) => date.add.days(monthStart, dayNumber);
	const isSelected = !value ? () => false : (day: Date) => date.isSameDay(value, day);
	const isHighlighted = !highlightToday ? () => false : (day: Date) => date.isSameDay(today, day);

	return (
		<div className={'grid grid-cols-7 grid-flow-dense gap-0.5 min-h-48'}>
			{range(0, firstWeekDay).map((ix) => (
				<div key={`empty_${ix}`} />
			))}
			{range(0, daysInMonth).map((dayNumber) => {
				const day = toDate(dayNumber);

				return (
					<button
						key={dayNumber}
						type={'button'}
						className={cn('text-center rounded-md enabled:hover:bg-skin-button-tonal-hover disabled:text-skin-muted', {
							'bg-skin-button-tonal-active font-semibold': isHighlighted(day),
							'bg-neutral-900 text-white hover:text-black disabled:bg-skin-button-tonal-muted': isSelected(day),
						})}
						onClick={() => onSelect(day)}
						disabled={(minDate && date.isBefore(day, minDate)) || (maxDate && date.isAfter(day, maxDate))}
					>
						{dayNumber + 1}
					</button>
				);
			})}
		</div>
	);
};
